/**
 * Whether an entity link click navigates or opens the preview (#9746).
 *
 * Single source of the decision. The RMS name cell consults it before
 * suppressing its anchor's default; `Layout` consults it when an
 * `openEntityPreview` event arrives from a surface that has no anchor to
 * suppress (inline entity cards, the note viewer, the document metadata
 * panel). Splitting the check across those two callers is unavoidable — one
 * has to decide before `preventDefault`, the other only ever sees the event —
 * so the rule itself lives here and neither caller reimplements it.
 *
 * Generalizes #9741, which established the same rule for entity detail pages
 * only. The rule now covers all of RMS.
 */

export interface EntityClickContext {
  /**
   * True when the click originated inside the entity preview pane. Entity-to-
   * entity browsing there pushes onto the preview's own stack; navigating
   * would throw away the pane the user is reading in.
   */
  insidePreviewPane: boolean;
}

const firstSegment = (pathname: string): string =>
  pathname.replace(/^\/+/, "").split("/")[0] ?? "";

/** True for any route under the RMS product. */
export function isRmsPath(pathname: string): boolean {
  return firstSegment(pathname) === "rms";
}

export function shouldNavigateToEntity(
  pathname: string,
  { insidePreviewPane }: EntityClickContext
): boolean {
  if (insidePreviewPane) return false;
  // Inside the RMS, a preview of an RMS record is redundant with the page the
  // user is already on. Outside it, previewing is the point: navigating would
  // drop them out of the conversation or report they were reading.
  return isRmsPath(pathname);
}
